import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactPage() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) {
      alert('お問い合わせ内容は500文字以内でご入力ください。');
      return;
    }
    setStatus('submitting');
    try {
      const data = new FormData(form);
      const params = new URLSearchParams();
      data.forEach((val, key) => params.append(key, val as string));
      const res = await fetch('https://y-f-s.jp/mail/contact.php', {        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const handleConsultSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const detail = (form.elements.namedItem('detail') as HTMLTextAreaElement)?.value ?? '';
    if (detail.length > 500) {
      alert('ご相談内容は500文字以内でご入力ください。');
      return;
    }
    setStatus('submitting');
    try {
      const data = new FormData(form);
      const params = new URLSearchParams();
      data.forEach((val, key) => params.append(key, val as string));
      const res = await fetch('https://y-f-s.jp/mail/contact.php', {        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-32 pb-16 bg-[#1a2332]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <span className="text-xs font-semibold tracking-[3px] text-white/40 uppercase">Contact</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">お問い合わせ</h1>
          <p className="text-base text-white/60 max-w-xl">
            サービスに関するご相談・ご質問はお気軽にお問い合わせください。
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#faf9f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {status === 'success' ? (
            <div className="max-w-2xl mx-auto text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1a2332] mx-auto mb-6">
                <i className="ri-check-line text-2xl text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#1a2332] mb-4">送信完了しました</h2>
              <p className="text-gray-600 mb-8">
                お問い合わせありがとうございます。<br />
                担当者より3営業日以内にご連絡いたします。
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="px-6 py-3 bg-[#1a2332] text-white text-sm font-medium rounded-lg hover:bg-[#2d3748] transition-colors cursor-pointer whitespace-nowrap"
              >
                戻る
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Info */}
              <div className="lg:col-span-1">
                <h2 className="text-xl font-bold text-[#1a2332] mb-6">ご連絡先</h2>
                <div className="space-y-5">
                  {[
                    { icon: 'ri-map-pin-line', label: '所在地', value: '〒116-0003\n東京都荒川区南千住8－6－2－1113' },
                    { icon: 'ri-phone-line', label: '電話番号', value: '090-2226-8377' },
                    { icon: 'ri-mail-line', label: 'メール', value: 'hayashi@y-f-s.jp' },
                    { icon: 'ri-time-line', label: '営業時間', value: '平日 9:00〜18:00' },
                  ].map((item) => (
                    <div key={item.label} className="flex gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1a2332]/10 flex-shrink-0">
                        <i className={`${item.icon} text-[#1a2332]`} />
                      </div>
                      <div>
                        <div className="text-xs text-gray-400 mb-0.5">{item.label}</div>
                        <div className="text-sm text-[#1a2332] whitespace-pre-line">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-10 bg-[#1a2332] rounded-2xl p-6">
                  <h3 className="text-base font-bold text-white mb-3">無料相談について</h3>
                  <p className="text-sm text-white/70 leading-relaxed">
                    初回のご相談は無料で承っております。
                    現状の課題や目標をヒアリングし、最適な支援策をご提案します。
                    お気軽にお申し込みください。
                  </p>
                </div>
              </div>

              {/* Forms */}
              <div className="lg:col-span-2 space-y-8">
                {/* Contact Form */}
                <div className="bg-white rounded-2xl p-8">
                  <h2 className="text-xl font-bold text-[#1a2332] mb-6">お問い合わせフォーム</h2>
                  <form
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          お名前 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="山田 太郎"
                          className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a2332] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          会社名
                        </label>
                        <input
                          type="text"
                          name="company"
                          placeholder="株式会社〇〇"
                          className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a2332] transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        メールアドレス <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="example@company.co.jp"
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a2332] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        電話番号
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="03-XXXX-XXXX"
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a2332] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        ご用件 <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="subject"
                        required
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a2332] transition-colors bg-white cursor-pointer"
                      >
                        <option value="">選択してください</option>
                        <option value="集客コンサルティングについて">集客コンサルティングについて</option>
                        <option value="営業支援コンサルティングについて">営業支援コンサルティングについて</option>
                        <option value="料金・プランについて">料金・プランについて</option>
                        <option value="無料相談の申し込み">無料相談の申し込み</option>
                        <option value="その他">その他</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        お問い合わせ内容 <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="お問い合わせ内容をご記入ください"
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a2332] transition-colors resize-none"
                      />
                      <div className="text-right text-xs text-gray-400 mt-1">{charCount}/500文字</div>
                    </div>
                    {status === 'error' && (
                      <p className="text-sm text-red-500">送信に失敗しました。時間をおいて再度お試しください。</p>
                    )}
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full py-4 bg-[#1a2332] text-white font-semibold rounded-lg hover:bg-[#2d3748] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {status === 'submitting' ? '送信中...' : '送信する'}
                    </button>
                  </form>
                </div>

                {/* Free Consultation Form */}
                <div className="bg-[#1a2332] rounded-2xl p-8">
                  <h2 className="text-xl font-bold text-white mb-2">無料相談を申し込む</h2>
                  <p className="text-sm text-white/60 mb-6">現状の課題をお聞かせください。最適な支援策をご提案します。</p>
                  <form
                    id="consult-form"
                    data-readdy-form
                    onSubmit={handleConsultSubmit}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-1.5">
                          お名前 <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="山田 太郎"
                          className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-lg focus:outline-none focus:border-white/60 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-1.5">
                          会社名
                        </label>
                        <input
                          type="text"
                          name="company"
                          placeholder="株式会社〇〇"
                          className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-lg focus:outline-none focus:border-white/60 transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1.5">
                        メールアドレス <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="example@company.co.jp"
                        className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-lg focus:outline-none focus:border-white/60 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1.5">
                        ご相談内容 <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        name="detail"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="現状の課題や相談したい内容をご記入ください（500文字以内）"
                        className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-lg focus:outline-none focus:border-white/60 transition-colors resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full py-4 bg-white text-[#1a2332] font-semibold rounded-lg hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {status === 'submitting' ? '送信中...' : '無料相談を申し込む'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
